/**
 * The asset tree (piece 28, The asset register): one self-referencing
 * `AssetNode` table for every level, from a whole plant down to one fitted
 * part. 🔨 Unlimited depth comes from nesting the Grouping kinds, never from a
 * table per level, and a Component is the leaf nothing sits beneath.
 * 🔨 A node is never its own ancestor: the database refuses the cycle whatever
 * path wrote it, and the write path says why before it asks.
 */
import type { TreeNode } from '../composites/treeList';
import { fill, t, type StringKey } from '../strings';

export const ASSET_NODE_TYPES = ['AssetGroup', 'FunctionGrouping', 'ComponentGrouping', 'Component'] as const;
export type AssetNodeType = (typeof ASSET_NODE_TYPES)[number];

export function isAssetNodeType(value: unknown): value is AssetNodeType {
  return typeof value === 'string' && (ASSET_NODE_TYPES as readonly string[]).includes(value);
}

/**
 * 🔒 The colour per level (piece 28; the Asset Hierarchy Level Symbols
 * appendix): navy, teal, purple, amber, so depth reads independent of
 * indentation. Each is an existing colour token, never a raw colour. 🔒 The
 * asset tree fills each row with it across the row's full width, and the row's
 * text takes the ink its contrast chooses (decision 1290).
 */
export const ASSET_LEVEL_BAR: Readonly<Record<AssetNodeType, string>> = {
  AssetGroup: 'var(--navy-primary)',
  FunctionGrouping: 'var(--tag-teal-text)',
  ComponentGrouping: 'var(--tag-purple-text)',
  Component: 'var(--tag-amber-text)',
};

const TYPE_LABEL: Readonly<Record<AssetNodeType, StringKey>> = {
  AssetGroup: 'asset.type.assetGroup',
  FunctionGrouping: 'asset.type.functionGrouping',
  ComponentGrouping: 'asset.type.componentGrouping',
  Component: 'asset.type.component',
};

/** The level's name as a person reads it. */
export function assetTypeLabel(type: AssetNodeType): string {
  return t(TYPE_LABEL[type]);
}

export interface AssetNodeShape {
  id: string;
  parentNodeId: string | null;
  nodeType: AssetNodeType;
  name: string;
  sortOrder: number;
}

/** Every node beneath `id`, however deep, so a move beneath one of them can be refused. */
export function descendantIds(nodes: readonly Pick<AssetNodeShape, 'id' | 'parentNodeId'>[], id: string): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const node of nodes) {
    if (!node.parentNodeId) continue;
    byParent.set(node.parentNodeId, [...(byParent.get(node.parentNodeId) ?? []), node.id]);
  }
  const found = new Set<string>();
  const walk = (parent: string) => {
    for (const child of byParent.get(parent) ?? []) {
      if (found.has(child)) continue;
      found.add(child);
      walk(child);
    }
  };
  walk(id);
  return found;
}

/**
 * Why a node cannot sit beneath the parent asked for, or null where it can.
 * 🔨 Null is the top of the register. A Component parents nothing, a node is
 * never its own parent, and a node never moves beneath its own descendant.
 */
export function parentProblem(
  nodes: readonly Pick<AssetNodeShape, 'id' | 'parentNodeId'>[],
  node: { id: string | null },
  parent: Pick<AssetNodeShape, 'id' | 'nodeType'> | null,
): string | null {
  if (!parent) return null;
  if (parent.nodeType === 'Component') return 'a Component is the leaf of the tree and holds nothing beneath it; place the node under a grouping instead';
  if (node.id === null) return null;
  if (parent.id === node.id) return 'a node cannot sit beneath itself';
  if (descendantIds(nodes, node.id).has(parent.id)) return 'a node cannot sit beneath one of its own descendants, which would close the tree into a loop';
  return null;
}

/** Whether the whole set is a tree: every parent named exists, and no walk upward returns to where it started. */
export function hierarchyProblems(nodes: readonly Pick<AssetNodeShape, 'id' | 'parentNodeId' | 'nodeType'>[]): { orphans: string[]; cycles: string[]; componentParents: string[] } {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const orphans = nodes.filter((node) => node.parentNodeId !== null && !byId.has(node.parentNodeId)).map((node) => node.id);
  const cycles: string[] = [];
  for (const node of nodes) {
    const seen = new Set<string>([node.id]);
    let at = node.parentNodeId ? byId.get(node.parentNodeId) : undefined;
    while (at) {
      if (seen.has(at.id)) {
        cycles.push(node.id);
        break;
      }
      seen.add(at.id);
      at = at.parentNodeId ? byId.get(at.parentNodeId) : undefined;
    }
  }
  const componentParents = nodes.filter((node) => node.parentNodeId !== null && byId.get(node.parentNodeId)?.nodeType === 'Component').map((node) => node.id);
  return { orphans, cycles, componentParents };
}

/** A new node takes the next explicit position among its siblings, never an implicit creation-date order. */
export function nextSortOrder(siblings: readonly Pick<AssetNodeShape, 'sortOrder'>[]): number {
  return siblings.reduce((max, sibling) => Math.max(max, sibling.sortOrder), -1) + 1;
}

/** The rows the shared Nested Tree List draws, ordered by `sortOrder` then name, each with its level's bar. */
export function assetTreeNodes<N extends AssetNodeShape>(nodes: readonly N[], label: (node: N) => string = (node) => node.name): (TreeNode & { node: N })[] {
  const ids = new Set(nodes.map((node) => node.id));
  return [...nodes]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    .map((node) => ({
      id: node.id,
      // A parent outside the rows drawn is drawn at the top, so a filtered tree never loses a node.
      parentId: node.parentNodeId && ids.has(node.parentNodeId) ? node.parentNodeId : null,
      label: label(node),
      barColor: ASSET_LEVEL_BAR[node.nodeType],
      node,
    }));
}

/** The chain from the top of the register down to `id`, for the panel's breadcrumb. */
export function ancestry<N extends Pick<AssetNodeShape, 'id' | 'parentNodeId'>>(nodes: readonly N[], id: string): N[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const chain: N[] = [];
  const seen = new Set<string>();
  let at = byId.get(id);
  while (at && !seen.has(at.id)) {
    seen.add(at.id);
    chain.unshift(at);
    at = at.parentNodeId ? byId.get(at.parentNodeId) : undefined;
  }
  return chain;
}

/** A node as the site question reads it: its parent and the site stored on it, which only a node with no parent carries. */
export interface SitedNode {
  id: string;
  parentNodeId: string | null;
  siteId: string | null;
}

/**
 * 🔒 A node's site is its root ancestor's (decision 0780): the owner's words,
 * *"the location is effectively the parent unless you are an asset class, then
 * it is the site."* The walk follows `parentNodeId` to the top and reads the
 * `siteId?` stored there, so a child never stores a site and a move carries
 * the whole subtree. Null where the root stands in stores or on a truck, or
 * where the chain is broken before it reaches a root.
 */
export function effectiveSiteOf(nodes: ReadonlyMap<string, SitedNode> | readonly SitedNode[], id: string): string | null {
  const byId: ReadonlyMap<string, SitedNode> = nodes instanceof Map ? nodes : new Map((nodes as readonly SitedNode[]).map((node) => [node.id, node]));
  const seen = new Set<string>();
  let at = byId.get(id);
  while (at && !seen.has(at.id)) {
    seen.add(at.id);
    if (at.parentNodeId === null) return at.siteId;
    at = byId.get(at.parentNodeId);
  }
  return null;
}

/**
 * 🔨 Why a site cannot be written on this node, or null where it can: only a
 * node with no parent stores one (decision 0780). The refusal names the node
 * the site belongs on.
 */
export function siteOnChildProblem(parentNodeId: string | null, siteId: string | null | undefined, rootName: string | null): string | null {
  if (parentNodeId === null || !siteId) return null;
  return `a node beneath another takes its site from the top of its tree${rootName ? ` (${rootName})` : ''}; set the site there, never on a node with a parent`;
}

/** The tree's heading for one site: never an `AssetNode`, a row the tree draws above the site's top-level nodes (decision 0780). */
export interface AssetSiteHeading {
  id: string;
  name: string;
  companyName: string;
}

/**
 * 🔒 Whether a tree is drawn on the Assets page, the organization's own register
 * (decision 12540, amending 0780). A tree is placed by its top node: drawn there
 * when its owner is the organization's own Company (decision 0132), or when it
 * has no owner and stands at no site or at one of the organization's own sites.
 * A tree another company owns is drawn on its Site page alone. 🔨 Nothing is
 * stored: the split decides where a node is drawn, never where it lives.
 */
export function drawnOnOwnRegister(root: { ownerCompanyId: string | null; siteCompanyId: string | null }, ownCompanyId: string | null): boolean {
  if (root.ownerCompanyId !== null) return ownCompanyId !== null && root.ownerCompanyId === ownCompanyId;
  return root.siteCompanyId === null || (ownCompanyId !== null && root.siteCompanyId === ownCompanyId);
}

/** How many assets stand somewhere, in words (decisions 1420, 12540): a site heading and a Company page's Sites row read it. */
export function assetCountText(count: number): string {
  return count === 1 ? t('asset.site.countOne') : fill(t('asset.site.countMany'), { count: String(count) });
}

/** 🔒 The tree read's narrowing to the Assets page's own register (decision 12540): `GET /assets?own=1`. */
export const ASSET_OWN_REGISTER = '1';

/** The id the *No site* heading carries, for what sits in stores or on a truck. */
export const ASSET_NO_SITE_ROW = 'site:none';

/** The id a site heading carries in the tree, distinct from every node id. */
export function assetSiteRowId(siteId: string | null): string {
  return siteId ? `site:${siteId}` : ASSET_NO_SITE_ROW;
}

export type AssetTreeEntry<N> = (TreeNode & { node: N; site?: undefined }) | (TreeNode & { node?: undefined; site: AssetSiteHeading | null });

/**
 * 🔒 The tree starts at the site (decision 0780). One heading per site that
 * holds a top-level node, in name order, then one *No site* heading at the end
 * where anything sits nowhere; beneath each stand its top-level nodes, then
 * the tree as before. A node drawn at the top of a narrowed tree files under
 * its own effective site, which `siteOf` answers.
 */
export function assetTreeWithSites<N extends AssetNodeShape>(
  nodes: readonly N[],
  sites: readonly AssetSiteHeading[],
  siteOf: (node: N) => string | null,
  label: (node: N) => string = (node) => node.name,
): AssetTreeEntry<N>[] {
  const drawn = assetTreeNodes(nodes, label);
  const tops = drawn.filter((entry) => entry.parentId === null);
  const used = new Set(tops.map((entry) => siteOf(entry.node)));
  const known = new Map(sites.map((site) => [site.id, site]));
  const headings: AssetTreeEntry<N>[] = [...used]
    .filter((id): id is string => id !== null)
    .map((id) => known.get(id) ?? { id, name: id, companyName: '' })
    .sort((a, b) => a.name.localeCompare(b.name) || a.companyName.localeCompare(b.companyName))
    .map((site) => ({ id: assetSiteRowId(site.id), parentId: null, label: site.name, site }));
  if (used.has(null)) headings.push({ id: ASSET_NO_SITE_ROW, parentId: null, label: t('asset.site.none'), site: null });
  return [
    ...headings,
    ...drawn.map((entry) => (entry.parentId === null ? { ...entry, parentId: assetSiteRowId(siteOf(entry.node)) } : entry)),
  ];
}

/** 🔨 The record route is permanent from the day a node is created (piece 2; piece 28): `/assets/:id`, never another shape. */
export const ASSETS_ROUTE = '/assets';
export function assetRoute(id: string): string {
  return `${ASSETS_ROUTE}/${id}`;
}
